import React from 'react';
import { BarChart3, CalendarPlus, CheckCircle2, Clock, Megaphone, Users, XCircle } from 'lucide-react';
import type { RoutyDaySummaryArtifact } from '../../business/routy/routyArtifacts';

/**
 * The daily summary card: totals at a glance, one row per team member
 * (company scope), and the day's top lead sources — so "give me a daily
 * summary" reads as a card instead of a paragraph.
 */
const DaySummaryCard: React.FC<{ artifact: RoutyDaySummaryArtifact }> = ({ artifact }) => {
  const { totals } = artifact;
  const stats: Array<{ label: string; value: number; className: string }> = [
    { label: 'Appointments', value: totals.appointments, className: 'text-ink-900' },
    { label: 'Completed', value: totals.completed, className: 'text-emerald-600' },
    { label: 'Remaining', value: totals.remaining, className: 'text-blue-600' },
    ...(totals.cancelled > 0 ? [{ label: 'Cancelled', value: totals.cancelled, className: 'text-red-500' }] : []),
  ];

  return (
    <div className="ml-10 rounded-lg bg-surface border border-ink-200 shadow-sm overflow-hidden max-w-md">
      <div className="px-4 py-2.5 bg-ink-50 border-b border-ink-200 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-blue-600" />
        <span className="text-sm font-semibold text-ink-900 truncate">Daily Summary · {artifact.dateLabel}</span>
        <span className="ml-auto flex-shrink-0 rounded-md bg-ink-100 text-ink-500 text-2xs font-semibold px-2 py-0.5 uppercase tracking-wide">
          {artifact.scope === 'company' ? 'Company' : 'Your day'}
        </span>
      </div>

      <div className="p-3 space-y-3">
        {/* Totals */}
        <div className={`grid gap-2 ${stats.length === 4 ? 'grid-cols-4' : 'grid-cols-3'}`}>
          {stats.map((s) => (
            <div key={s.label} className="rounded-lg border border-ink-100 bg-ink-50/60 px-2 py-2 text-center">
              <p className={`text-lg font-bold leading-none ${s.className}`}>{s.value}</p>
              <p className="mt-1 text-2xs text-ink-400 leading-none">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Per-member rows (company scope) */}
        {artifact.members && artifact.members.length > 0 && (
          <div>
            <p className="inline-flex items-center gap-1.5 text-2xs font-semibold text-ink-400 uppercase tracking-wider mb-1.5">
              <Users className="h-3.5 w-3.5" />
              Team
            </p>
            <div className="space-y-1.5">
              {artifact.members.map((m) => (
                <div key={m.name} className="rounded-lg border border-ink-100 bg-surface px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-ink-800 truncate">{m.name}</p>
                    <span className="flex-shrink-0 inline-flex items-center gap-2 text-2xs">
                      <span className="inline-flex items-center gap-0.5 text-emerald-600 font-semibold">
                        <CheckCircle2 className="h-3 w-3" />
                        {m.completed}
                      </span>
                      {m.remaining > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-blue-600 font-semibold">
                          <Clock className="h-3 w-3" />
                          {m.remaining}
                        </span>
                      )}
                      {m.cancelled > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-red-500 font-semibold">
                          <XCircle className="h-3 w-3" />
                          {m.cancelled}
                        </span>
                      )}
                    </span>
                  </div>
                  {m.completedLabel && (
                    <p className="mt-0.5 text-2xs text-ink-500 truncate">Completed: {m.completedLabel}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New bookings created today — a different axis from the totals
            above (which count appointments HAPPENING today): a booking that
            came in today for next Wednesday belongs here. */}
        {artifact.bookedToday && artifact.bookedToday.count > 0 && (
          <div>
            <p className="inline-flex items-center gap-1.5 text-2xs font-semibold text-ink-400 uppercase tracking-wider mb-1.5">
              <CalendarPlus className="h-3.5 w-3.5" />
              Booked today · {artifact.bookedToday.count}
            </p>
            {artifact.bookedToday.items && artifact.bookedToday.items.length > 0 && (
              <div className="space-y-1.5">
                {artifact.bookedToday.items.map((b, i) => (
                  <div key={i} className="rounded-lg border border-ink-100 bg-surface px-3 py-2 flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-ink-800 truncate">
                      {b.customer}
                      {b.service && <span className="font-normal text-ink-500"> — {b.service}</span>}
                    </p>
                    {b.whenLabel && (
                      <span className="flex-shrink-0 text-2xs text-ink-400">{b.whenLabel}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Top lead sources (bookings created today) */}
        {artifact.leadSources && artifact.leadSources.length > 0 && (
          <div>
            <p className="inline-flex items-center gap-1.5 text-2xs font-semibold text-ink-400 uppercase tracking-wider mb-1.5">
              <Megaphone className="h-3.5 w-3.5" />
              Top lead sources today
            </p>
            <div className="flex flex-wrap gap-1.5">
              {artifact.leadSources.map((l) => (
                <span
                  key={l.source}
                  className="inline-flex items-center gap-1 rounded-md bg-blue-50 border border-blue-200/70 text-blue-700 text-2xs font-medium px-2 py-1"
                >
                  {l.source}
                  <span className="font-bold">{l.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DaySummaryCard;
